import {
  Injectable,
  linkedSignal,
  signal,
  WritableSignal,
} from '@angular/core';
import { ShoppingCartItem } from '../models/shopping';
import { arraysEqual } from '../utils/array';
import { ShoppingApiService } from './apis/shopping-api.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  items: WritableSignal<ShoppingCartItem[]> = signal<ShoppingCartItem[]>([]);
  subtotal: WritableSignal<number> = linkedSignal<number>(() => {
    return this.items().reduce((total, item) => {
      return total + item.amount;
    }, 0);
  });
  quantity: WritableSignal<number> = linkedSignal<number>(() => {
    return this.items().reduce((total, item) => {
      return total + item.quantity;
    }, 0);
  });
  constructor(private shoppingApi: ShoppingApiService) {}

  /**
   * Retrieves shopping cart items from the API and updates the local state.
   *
   * @param t - 在SSR中多次调用会导致后续调用不被使用，设置t可以避免
   * @returns A promise that resolves to an array of ShoppingCartItem.
   */
  async findShoppingCartItems(t?: number): Promise<ShoppingCartItem[]> {
    const r = await this.shoppingApi.findShoppingCartItems(t);
    this.items.set(r);
    return r;
  }

  updateShoppingCartItem(newItem: ShoppingCartItem) {
    this.items.update((items) => {
      const newItems: ShoppingCartItem[] = [];
      var isNew = true;
      for (const item of items) {
        if (
          item.product_id == newItem.product_id &&
          item.product_priced_attr_value_id ==
            newItem.product_priced_attr_value_id &&
          arraysEqual(
            item.product_attr_value_ids,
            newItem.product_attr_value_ids
          )
        ) {
          newItems.push(newItem);
          isNew = false;
        } else {
          newItems.push(item);
        }
      }
      return isNew ? [...items, newItem] : newItems;
    });
  }

  removeShoppingCartItem(
    product_id: number,
    product_attr_value_ids: number[],
    product_priced_attr_value_id: number
  ) {
    this.items.update((items) => {
      return items.filter((i) => {
        return (
          i.product_id != product_id ||
          !arraysEqual(i.product_attr_value_ids, product_attr_value_ids) ||
          i.product_priced_attr_value_id != product_priced_attr_value_id
        );
      });
    });
  }

  async addShoppingCartItem({
    product_id,
    quantity,
    product_attr_value_ids,
    product_priced_attr_value_id,
  }: {
    product_id: number;
    quantity: number;
    product_attr_value_ids: number[];
    product_priced_attr_value_id: number;
  }): Promise<ShoppingCartItem> {
    const r = await this.shoppingApi.addShoppingCartItem({
      product_id,
      quantity,
      product_attr_value_ids,
      product_priced_attr_value_id,
    });
    this.updateShoppingCartItem(r);
    return r;
  }

  async setShoppingCartItem({
    product_id,
    quantity,
    product_attr_value_ids,
    product_priced_attr_value_id,
  }: {
    product_id: number;
    quantity: number;
    product_attr_value_ids: number[];
    product_priced_attr_value_id: number;
  }): Promise<ShoppingCartItem | null> {
    const r = await this.shoppingApi.setShoppingCartItem({
      product_id,
      quantity,
      product_attr_value_ids,
      product_priced_attr_value_id,
    });
    if (r) {
      this.updateShoppingCartItem(r);
    } else {
      this.removeShoppingCartItem(
        product_id,
        product_attr_value_ids,
        product_priced_attr_value_id
      );
    }
    return r;
  }
}
